import { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Warning, Equals, NotePencil } from "@phosphor-icons/react";
import TradeFormDialog from "../components/TradeFormDialog";
import { fmtNum } from "../lib/format";

function Field({ label, unit, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="label-caps text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-muted-foreground font-mono w-4">{unit}</span>
        <Input data-testid={testid} type="number" step="any" className="font-mono"
          value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

export default function Calculator() {
  const [account, setAccount] = useState("");
  const [risk, setRisk] = useState("");
  const [side, setSide] = useState("long");
  const [entry, setEntry] = useState("");
  const [tp, setTp] = useState("");
  const [sl, setSl] = useState("");
  const [output, setOutput] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const prefill = output?.result ? {
    side: output.result.side,
    entry_price: output.result.entry,
    quantity: +output.result.qty.toFixed(8),
    stop_loss: output.result.sl,
    take_profits: output.result.tp != null ? [{ trigger_type: "price", value: output.result.tp, close_size_pct: "" }] : [],
  } : null;

  const calculate = () => {
    const a = parseFloat(account), r = parseFloat(risk), e = parseFloat(entry),
      t = parseFloat(tp), s = parseFloat(sl);

    if (![a, r, e, s].every((v) => !isNaN(v) && v > 0)) {
      setOutput({ warning: "Fill in account size, max risk, entry price and stop loss (values must be above 0)." });
      return;
    }
    const slDist = side === "long" ? e - s : s - e;
    if (slDist <= 0) {
      setOutput({
        warning: side === "long"
          ? "For a long, the stop loss must be below the entry price."
          : "For a short, the stop loss must be above the entry price.",
      });
      return;
    }
    const riskAmount = (a * r) / 100;
    const qty = riskAmount / slDist;
    const contracts = qty * e;
    let profit = null, rrr = null, warning = null;
    if (!isNaN(t) && t > 0) {
      const tpDist = side === "long" ? t - e : e - t;
      if (tpDist <= 0) {
        warning = side === "long"
          ? "For a long, the take profit must be above the entry price."
          : "For a short, the take profit must be below the entry price.";
      } else {
        profit = qty * tpDist;
        rrr = profit / riskAmount;
      }
    }
    setOutput(warning ? { warning } : { result: { qty, contracts, riskAmount, profit, rrr, side, entry: e, sl: s, tp: !isNaN(t) && t > 0 ? t : null } });
  };

  const fmt = (n, d = 2) => fmtNum(n, d);
  const fmtQty = (n) => fmtNum(n, 8);

  const rrr = output?.result?.rrr;
  const rrrTone = rrr == null ? null : rrr < 1 ? "bad" : rrr < 1.5 ? "warn" : "good";
  const toneStyles = {
    bad: { boxCls: "border-[hsl(var(--loss))] bg-[hsl(var(--loss)/0.08)]", textCls: "text-loss",
           msg: "RRR below 1 — you risk more than you can win. Reconsider this trade." },
    warn: { boxCls: "border-yellow-500 bg-yellow-500/10", textCls: "text-yellow-500",
            msg: "RRR between 1 and 1.5 — marginal reward for the risk taken." },
    good: { boxCls: "border-[hsl(var(--profit))] bg-[hsl(var(--profit)/0.08)]", textCls: "text-profit",
            msg: "RRR of 1.5 or better — healthy reward-to-risk." },
  };
  const tone = rrrTone ? toneStyles[rrrTone] : null;

  return (
    <div className="max-w-4xl space-y-6" data-testid="calculator-page">
      <h1 className="font-heading font-bold text-3xl tracking-tight">Position Calculator</h1>
      <p className="text-sm text-muted-foreground -mt-3">
        Size your position from your account and max risk — the calculator gives you the exact quantity so a stop-out never loses more than your risk %.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="border border-border bg-card p-6 space-y-5">
          <Field label="Account size (stake for new position)" unit="$" value={account}
            onChange={setAccount} testid="calc-account-input" placeholder="For example: 5000" />
          <Field label="Max risk (decimals allowed, e.g. 0.5)" unit="%" value={risk} onChange={setRisk}
            testid="calc-risk-input" placeholder="For example: 1" />
          <div>
            <Label className="label-caps text-muted-foreground">Position type</Label>
            <Select value={side} onValueChange={setSide}>
              <SelectTrigger data-testid="calc-side-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="long">Long</SelectItem>
                <SelectItem value="short">Short</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Entry price" unit="$" value={entry} onChange={setEntry}
            testid="calc-entry-input" placeholder="For example: 20000" />
          <Field label="Take profit (TP)" unit="$" value={tp} onChange={setTp}
            testid="calc-tp-input" placeholder="For example: 22000" />
          <Field label="Stop loss" unit="$" value={sl} onChange={setSl}
            testid="calc-sl-input" placeholder="For example: 19000" />
          <Button data-testid="calc-calculate-btn" onClick={calculate}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
            <Equals size={16} className="mr-1" weight="bold" /> Calculate
          </Button>
        </div>

        <div className={`border p-6 space-y-1 ${tone ? tone.boxCls : "border-border bg-card"}`}
          data-testid="calc-results" data-rrr-tone={rrrTone || "none"}>
          <div className="label-caps text-muted-foreground mb-4">Result</div>
          {output?.warning ? (
            <p className="flex items-start gap-2 text-sm text-loss" data-testid="calc-warning">
              <Warning size={18} className="shrink-0 mt-0.5" /> {output.warning}
            </p>
          ) : output?.result ? (
            <div className="space-y-3 font-mono text-sm">
              <div className="flex justify-between border-b border-border pb-2">
                <span className="text-muted-foreground uppercase">{output.result.side} size</span>
                <span className="text-[hsl(var(--primary))] text-base" data-testid="calc-size">{fmtQty(output.result.qty)}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-2">
                <span className="text-muted-foreground">Contract value</span>
                <span data-testid="calc-contracts">${fmt(output.result.contracts)}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-2">
                <span className="text-muted-foreground">Loss at stop</span>
                <span className="text-loss" data-testid="calc-loss">-${fmt(output.result.riskAmount)}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-2">
                <span className="text-muted-foreground">Profit at TP</span>
                <span className="text-profit" data-testid="calc-profit">
                  {output.result.profit != null ? `+$${fmt(output.result.profit)}` : "—"}
                </span>
              </div>
              <div className="flex justify-between pb-1">
                <span className="text-muted-foreground">RRR (reward : risk)</span>
                <span className={`text-base ${tone ? tone.textCls : ""}`} data-testid="calc-rrr">
                  {output.result.rrr != null ? fmt(output.result.rrr) : "—"}
                </span>
              </div>
              {tone && (
                <p className={`flex items-start gap-2 text-xs pt-2 font-sans ${tone.textCls}`} data-testid="calc-rrr-msg">
                  <Warning size={15} className={`shrink-0 mt-0.5 ${rrrTone === "good" ? "hidden" : ""}`} />
                  {tone.msg}
                </p>
              )}
              <div className="pt-3">
                <Button data-testid="calc-use-in-trade-btn" onClick={() => setFormOpen(true)}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
                  <NotePencil size={16} className="mr-1" weight="bold" /> Use in new trade
                </Button>
                <p className="text-[11px] text-muted-foreground mt-1 font-sans text-center">
                  Opens the trade form pre-filled with this size, entry, SL and TP — just add the symbol.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="calc-empty">
              Fill in the fields and press Calculate to size your position.
            </p>
          )}
        </div>
      </div>

      <TradeFormDialog open={formOpen} onOpenChange={setFormOpen} prefill={prefill} />
    </div>
  );
}
